import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import Modal from "react-modal";
import styles from "./NewsletterModal.module.scss";
import IconAlertTriangle from "../icons/IconAlertTriangle";
import IconNo from "../icons/IconNo";
import IconValidateCheck from "../icons/IconValidateCheck";

import _ from "../../../i18n";
import Button from "../Button";
import PlansNewsletter from "./PlansNewsletter";
import NewsletterSettings from "./NewsletterSettings";
import IconClose from "../icons/IconClose";
import Tippy from "@tippyjs/react";
import { useQuery } from "react-query";
import {
  getCollaborators,
  getIdentitiesVerificationStatus,
  saveAppsSettings,
  registerDomainSES,
  checkDomainSES,
} from "../../../api";
import { DAYS } from "../../../config";
import { toast } from "react-toastify";
import { setFiduciary } from "../../../store";
import classNames from "classnames";

const preferences = [
  {
    index: "frequency",
    details: [
      {
        option: _("articles_option_3"),
        desc: _("frequency_desc"),
      },
    ],
  },
  {
    index: "recipients",
    details: [
      {
        option: _("recipient"),
        desc: _("recipients_desc"),
      },
    ],
  },
  {
    index: "senders",
    details: [
      {
        option: _("senders"),
        desc: _("senders_desc"),
      },
    ],
  },
  {
    index: "articles",
    desc: _("articles_option_title"),
    details: [
      {
        option: _("articles_option_1"),
        desc: _("fff_news_desc"),
      },
      {
        option: _("articles_option_2"),
        desc: _("my_pub_desc"),
      },
    ],
  },
  {
    index: "customization",
    desc: _("customization_option_title"),
    details: [
      {
        option: _("customization_option_1"),
        desc: _("pub_logo_desc"),
      },
      {
        option: _("customization_option_2"),
        desc: _("theme_desc"),
      },
    ],
  },
];

const frequency_tabs = [
  { value: "BY_WEEK", label: _("by_week") },
  { value: "BY_TWO_WEEKS", label: _("by_two_weeks") },
  { value: "BY_MONTH", label: _("by_month") },
];

const defaultConfig = {
  newslettersPerPeriod: 1,
  languages: [{ id: 1, language: "FR" }],
  planningDay: DAYS[0],
  planningHour: { value: "08", label: "08" },
  planningMinutes: { value: "00", label: "00" },
  schedules: [
    {
      id: 1,
      day: DAYS[0],
      hour: { value: "08", label: "08" },
      minutes: { value: "00", label: "00" },
    },
  ],
};
const NewsletterModal = ({ isOpen, setIsOpen, title, inProcess }) => {
  const dispatch = useDispatch();
  const fiduciary = useSelector((state) => state.folder.fiduciary);
  const auth = useSelector((state) => state.auth);
  const { token } = auth;
  const [tools, setTools] = useState([]);
  const [newsletterPlan, setNewsletterPlan] = useState("LITE");
  const [frequency, setFrequency] = useState([
    { value: "BY_TWO_WEEKS", label: _("by_two_weeks") },
  ]);
  const [slide, setSlide] = useState(1);
  const [selectedDay, setSelectedDay] = useState(DAYS[0]);
  const [isAutomaticNewsletter, setIsAutomaticNewsletter] = useState(false);
  const [selectedWeek, setSelectedWeek] = useState(null);
  const [selectedHour, setSelectedHour] = useState({
    value: "00",
    label: "00",
  });
  const [selectedMinutes, setSelectedMinutes] = useState({
    value: "00",
    label: "00",
  });
  const [sort, setSort] = useState({
    property: "lastName",
    dir: "asc",
  });
  const [senders, setSenders] = useState([]);
  const [hasPlans, setHasPlans] = useState(false);
  const [verifiedEmailsList, setVerifiedEmailsList] = useState([]);
  const [isVerifiedEmailsLoading, setIsVerifiedEmailsLoading] = useState(false);
  const [isLogoUaVisible, setIsLogoUaVisible] = useState(true);
  const [basicPlan, setBasicPlan] = useState("");
  const [activeFrequencyTab, setActiveFrequencyTab] = useState(null);

  const [frequencyConfigs, setFrequencyConfigs] = useState({
    BY_WEEK: { ...defaultConfig },
  });

  const resolvedTab = activeFrequencyTab ?? frequency[0]?.value;
  const activeConfig = frequencyConfigs[resolvedTab] ?? defaultConfig;
  const isRestoringRef = React.useRef(false);

  const [domains, setDomains] = useState({});
  const [selectedDomain, setSelectedDomain] = useState(null);
  const [isVerifying, setIsVerifying] = useState(false);

  const verifyDomain = async (email) => {
    const domain = email.split("@")[1];
    if (!domain) return;

    if (domains[domain]?.status === "SUCCESS") return;

    const result = await checkDomainSES(domain);

    setDomains((prev) => ({
      ...prev,
      [domain]: {
        status: result.verified
          ? "SUCCESS"
          : result.status === "pending"
            ? "PENDING"
            : "UNREGISTERED",
        data: result,
      },
    }));
  };

  useEffect(() => {
    if (!fiduciary) return setTools([]);

    const prefs = fiduciary.newsletterPreferences;
    if (!prefs?.apps) return setTools([]);

    setTools(prefs.apps);

    const nlTool = prefs.apps.find((t) => t.type === "BLOG_NL_CREATE");
    if (!nlTool) return;
    setHasPlans(!!nlTool.config);
    setNewsletterPlan(nlTool.config ?? "LITE");
    setBasicPlan(nlTool.basicPlan ?? "");
    setSelectedWeek({
      value: nlTool.selectedWeek ?? "FIRST_WEEK",
      label: nlTool.selectedWeek
        ? nlTool.frequency?.includes("BY_TWO_WEEKS")
          ? nlTool.selectedWeek === "FIRST_WEEK"
            ? _("week_desc_1")
            : _("week_desc_2")
          : _(nlTool.selectedWeek.toLowerCase())
        : _("first_week"),
    });
    setSenders(nlTool.senders ?? []);
    setIsAutomaticNewsletter(nlTool.isAutomatic ?? false);
    const restoredFrequencies = Array.isArray(nlTool.frequency)
      ? nlTool.frequency
      : nlTool.frequency
        ? [nlTool.frequency]
        : ["BY_WEEK"];

    const restoredConfigs = {};
    restoredFrequencies.forEach((freqValue) => {
      const freqNewsletters =
        nlTool.newsletters?.filter((nl) => nl.frequency === freqValue) ?? [];

      restoredConfigs[freqValue] = {
        newslettersPerPeriod: freqNewsletters.length || 1,
        themeId: freqNewsletters[0]?.themeId ?? null,
        // newsletter's own day/hour, NOT global planningDay
        planningDay:
          freqNewsletters.length === 1
            ? (DAYS.find((d) => d.value === freqNewsletters[0].day) ??
              DAYS.find((d) => d.value === Number(freqNewsletters[0].day)) ??
              DAYS[0])
            : (DAYS.find((d) => d.value === nlTool.planningDay) ?? DAYS[0]),

        planningHour:
          freqNewsletters.length === 1
            ? {
                value: String(freqNewsletters[0].hour).padStart(2, "0"),
                label: String(freqNewsletters[0].hour).padStart(2, "0"),
              }
            : {
                value: String(nlTool.planningHour ?? "08").padStart(2, "0"),
                label: String(nlTool.planningHour ?? "08").padStart(2, "0"),
              },

        planningMinutes:
          freqNewsletters.length === 1
            ? {
                value: String(freqNewsletters[0].minute).padStart(2, "0"),
                label: String(freqNewsletters[0].minute).padStart(2, "0"),
              }
            : {
                value: String(nlTool.planningMinutes ?? "00").padStart(2, "0"),
                label: String(nlTool.planningMinutes ?? "00").padStart(2, "0"),
              },

        languages: freqNewsletters.length
          ? freqNewsletters.map((nl) => ({
              id: nl.index,
              language: nl.language,
            }))
          : [{ id: 1, language: "FR" }],

        schedules: freqNewsletters.length
          ? freqNewsletters.map((nl) => {
              const matchedDay =
                DAYS.find((d) => d.value === nl.day) ??
                DAYS.find((d) => d.value === Number(nl.day)) ??
                DAYS[0];
              const hourStr = String(nl.hour).padStart(2, "0");
              const minuteStr = String(nl.minute).padStart(2, "0");
              return {
                id: nl.index,
                day: matchedDay,
                hour: { value: hourStr, label: hourStr },
                minutes: { value: minuteStr, label: minuteStr },
                themeId: nl.themeId ?? null,
              };
            })
          : [
              {
                id: 1,
                day: DAYS[0],
                hour: { value: "08", label: "08" },
                minutes: { value: "00", label: "00" },
              },
            ],
      };
    });
    isRestoringRef.current = true;
    setFrequencyConfigs(restoredConfigs);
    setActiveFrequencyTab(restoredFrequencies[0] ?? null);
    setFrequency(
      nlTool.frequency
        ? Array.isArray(nlTool.frequency)
          ? nlTool.frequency
              .map((f) => frequency_tabs.find((t) => t.value === f))
              .filter(Boolean)
          : [frequency_tabs.find((t) => t.value === nlTool.frequency)].filter(
              Boolean
            )
        : nlTool.config && !["PERSONALIZED", "STANDARD"].includes(nlTool.config)
          ? [{ value: "BY_TWO_WEEKS", label: _("by_two_weeks") }]
          : [{ value: "BY_WEEK", label: _("by_week") }]
    );
    if (nlTool.isLogoUaVisible !== undefined)
      setIsLogoUaVisible(nlTool.isLogoUaVisible);
  }, [fiduciary]);
  useEffect(() => {
    if (hasPlans && fiduciary.uaMode === "OECCBB_MEMBER") {
      setSlide(2);
    } else {
      setSlide(1);
    }
  }, [hasPlans]);
  useEffect(() => {
    if (isOpen) {
      handleFetchVerifiedEmails();
    }
  }, [isOpen]);

  useEffect(() => {
    if (!verifiedEmailsList.length || !senders.length) return;

    setSenders((prev) =>
      prev.map((sender) => {
        const match = verifiedEmailsList.find((e) => e.email === sender.email);
        return {
          ...sender,
          status: match ? match.VerificationStatus : "Failed",
        };
      })
    );
  }, [verifiedEmailsList]);

  useEffect(() => {
    // Skip configs update when restoring from DB
    if (isRestoringRef.current) {
      isRestoringRef.current = false;
    } else {
      setFrequencyConfigs((prev) => {
        const activeKeys = frequency.map((f) => f.value);

        const cleaned = Object.fromEntries(
          Object.entries(prev).filter(([key]) => activeKeys.includes(key))
        );

        frequency.forEach((f) => {
          if (!cleaned[f.value]) {
            cleaned[f.value] = { ...defaultConfig };
          }
        });

        return cleaned;
      });
    }

    // Always sync active tab with frequency
    if (!frequency.find((f) => f.value === activeFrequencyTab)) {
      setActiveFrequencyTab(frequency[0]?.value ?? null);
    }
  }, [frequency]);

  useEffect(() => {
    if (verifiedEmailsList.length > 0 && senders.length > 0) {
      senders.forEach((sender) => {
        const domain = sender.email.split("@")[1];

        const already = domains[domain]?.status;

        if (already) return;

        verifyDomain(sender.email);
      });
    }
  }, [verifiedEmailsList, senders]);

  let { data } = useQuery(
    ["listCollaboratorsNewsletters", token, sort, fiduciary, isOpen],
    async () => {
      if (token && fiduciary && isOpen) {
        const response = await getCollaborators({
          token,
          sort,
          organizationId: fiduciary.id,
          isConfigNewsletter: true,
        });
        return response.data;
      }
    }
  );

  const handleFetchVerifiedEmails = () => {
    setIsVerifiedEmailsLoading(true);
    getIdentitiesVerificationStatus(token)
      .then((resp) => {
        setVerifiedEmailsList(
          Object.keys(resp.data.data).map((el) => ({
            email: el,
            ...resp.data.data[el],
          }))
        );
      })
      .finally(() => setIsVerifiedEmailsLoading(false));
  };
  const handleCloseNLModal = () => {
    if (
      tools.filter((t) => t.type === "BLOG_NL_CREATE" && t.config === null)
        .length > 0 &&
      tools.filter((t) => t.type === "BLOG_NL_CREATE").length > 0
    ) {
      setTools(tools.filter((t) => t.type !== "BLOG_NL_CREATE"));
    }
    setIsOpen(false);
  };
  const handleVerifyDomain = async (email) => {
    const domain = email.split("@")[1];
    if (!domain) return;

    try {
      const result = await registerDomainSES(domain);

      setDomains((prev) => ({
        ...prev,
        [domain]: {
          status: "PENDING",
          data: result,
        },
      }));
    } catch (error) {
      console.error(error);
    }
  };
  const buildNlPayload = (base, updatedSenders) => ({
    ...base,
    config: newsletterPlan,
    frequency: frequency.map((f) => f.value),
    selectedWeek: selectedWeek?.value ?? "",
    senders: updatedSenders,
    isAutomatic: isAutomaticNewsletter,
    isLogoUaVisible,
    newsletters: Object.entries(frequencyConfigs).flatMap(
      ([freqValue, config]) => {
        if (config.newslettersPerPeriod === 1) {
          // Planning for single newsletter
          return [
            {
              index: 1,
              frequency: freqValue,
              language: config.languages[0]?.language ?? "FR",
              day: config.planningDay?.value ?? config.planningDay,
              hour: config.planningHour?.value ?? "08",
              minute: config.planningMinutes?.value ?? "00",
              themeId: config.themeId ?? null,
            },
          ];
        }
        return config.schedules.map((schedule, index) => ({
          index: index + 1,
          frequency: freqValue,
          language: config.languages[index]?.language ?? "FR",
          day: schedule.day?.value ?? schedule.day,
          hour: schedule.hour?.value ?? schedule.hour,
          minute: schedule.minutes?.value ?? schedule.minutes,
          themeId: schedule.themeId ?? null,
        }));
      }
    ),
  });

  const handleSave = async () => {
    try {
      const resp = await getIdentitiesVerificationStatus(token);
      const verifiedList = Object.keys(resp.data.data).map((el) => ({
        email: el,
        ...resp.data.data[el],
      }));
      setVerifiedEmailsList(verifiedList);

      const updatedSenders = senders.map((sender) => {
        const match = verifiedList.find((e) => e.email === sender.email);
        return {
          ...sender,
          status: match ? match.VerificationStatus : "Failed",
        };
      });
      setSenders(updatedSenders);

      if (newsletterPlan) {
        const toolsTabs =
          tools.length > 0
            ? tools.map((tool) =>
                tool.type === "BLOG_NL_CREATE"
                  ? buildNlPayload(tool, updatedSenders)
                  : tool
              )
            : [buildNlPayload({ type: "BLOG_NL_CREATE" }, updatedSenders)];

        setTools(toolsTabs);

        await saveAppsSettings({
          token: auth.token,
          communityId: fiduciary.id,
          data: {
            appName: "newsletter",
            tools: toolsTabs,
          },
        });
        toast.success(_("successfully_saved"));
        setIsOpen(false);
        const preferences = fiduciary.newsletterPreferences || {};
        dispatch(
          setFiduciary({
            ...fiduciary,
            newsletterPreferences: { ...preferences, apps: toolsTabs },
          })
        );
      }
    } catch (error) {
      toast.error(_("error"));
      setIsVerifiedEmailsLoading(false);
    }
  };
  const updateActiveConfig = (patch) => {
    setFrequencyConfigs((prev) => ({
      ...prev,
      [resolvedTab]: {
        ...(prev[resolvedTab] ?? activeConfig),
        ...patch,
      },
    }));
  };

  const renderDNSView = () => {
    const selectedDomainData = domains?.[selectedDomain]?.data || null;

    const records = selectedDomainData?.dns_records_to_add || [];

    const dkimRecords = records.filter((r) => r.purpose?.includes("DKIM"));
    const otherRecords = records.filter((r) => !r.purpose?.includes("DKIM"));

    const requiredRecords = otherRecords.filter(
      (r) => !r.purpose?.toLowerCase().includes("verification")
    );

    const optionalRecords = otherRecords.filter((r) =>
      r.purpose?.toLowerCase().includes("verification")
    );

    return (
      <div className={styles.dns_container}>
        <div className={styles.dns_modal_header}>
          <p className={styles.dns_modal_title}>{_("dns_records_to_add")}</p>
          <div className={styles.close} onClick={() => setSlide(2)}>
            <IconClose size={14} />
          </div>
        </div>

        {/* ALERT */}
        <div className={styles.dns_alert}>
          <span className={styles.dns_alert_icon}>
            <IconAlertTriangle />
          </span>

          <div>
            {_("dns_config_by_tech_team").map((line, i) => (
              <div key={i}>{i === 2 ? <strong>{line}</strong> : line}</div>
            ))}
          </div>
        </div>

        {selectedDomainData?.dns_records_to_add ? (
          <div className={styles.dns_grid}>
            <div className={styles.dns_col}>
              {requiredRecords.map((rec, i) => (
                <div key={i} className={styles.dns_record}>
                  <div>
                    <strong>Type:</strong> {rec.type}
                  </div>
                  <div>
                    <strong>Name:</strong> {rec.name}
                  </div>

                  <div>
                    <strong>Value:</strong>
                    <code className={styles.dns_value}>{rec.value}</code>
                  </div>

                  <div className={`${styles.dns_badge} ${styles.required}`}>
                    {_("required")}
                  </div>

                  <div className={styles.dns_purpose}>{rec.purpose}</div>
                </div>
              ))}

              {optionalRecords.map((rec, i) => (
                <div key={i} className={styles.dns_record}>
                  <div>
                    <strong>Type:</strong> {rec.type}
                  </div>
                  <div>
                    <strong>Name:</strong> {rec.name}
                  </div>

                  <div>
                    <strong>Value:</strong>
                    <code className={styles.dns_value}>{rec.value}</code>
                  </div>

                  <div className={`${styles.dns_badge} ${styles.optional}`}>
                    {_("optional")}
                  </div>

                  <div className={styles.dns_purpose}>{rec.purpose}</div>
                </div>
              ))}
            </div>

            {/*(DKIM) */}
            <div className={styles.dns_col}>
              {dkimRecords.length > 0 && (
                <div className={styles.dns_record}>
                  <div>
                    <strong>Type:</strong> CNAME (DKIM)
                  </div>

                  {dkimRecords.map((rec, i) => (
                    <div key={i}>
                      <strong>Name {i + 1}:</strong> {rec.name}
                      <br />
                      <strong>Value {i + 1}:</strong>
                      <code className={styles.dns_value}>{rec.value}</code>
                    </div>
                  ))}

                  <div className={`${styles.dns_badge} ${styles.required}`}>
                    {_("required")}
                  </div>

                  <div className={styles.dns_purpose}>
                    DKIM is required to sign your emails and improve
                    deliverability.
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : (
          <>
            <h3 style={{ margin: "16px 0 0 16px" }}>{_("dns_status")}</h3>

            <div className={styles.dns_grid}>
              <div className={styles.dns_col}>
                <div className={styles.dns_item}>
                  <span>TXT verification</span>
                  {selectedDomainData?.dns_status?.verification_txt_exists ? (
                    <IconValidateCheck />
                  ) : (
                    <IconNo />
                  )}
                </div>

                <div className={styles.dns_item}>
                  <span>SPF configured</span>
                  {selectedDomainData?.dns_status?.spf_includes_ses ? (
                    <IconValidateCheck />
                  ) : (
                    <IconNo />
                  )}
                </div>
              </div>

              <div className={styles.dns_col}>
                <div className={styles.dns_item}>
                  <span>DKIM records</span>
                  {selectedDomainData?.dns_status?.dkim_records_exist ? (
                    <IconValidateCheck />
                  ) : (
                    <IconNo />
                  )}
                </div>

                <div className={styles.dns_item}>
                  <span>DMARC</span>
                  {selectedDomainData?.dns_status?.dmarc_exists ? (
                    <IconValidateCheck />
                  ) : (
                    <IconNo />
                  )}
                </div>
              </div>
            </div>

            <h3 style={{ marginLeft: "16px" }}>{_("tokens_to_use")}</h3>

            <div className={styles.dns_grid}>
              <div className={styles.dns_col}>
                <div className={styles.dns_record}>
                  <div className={styles.dns_header}>
                    <strong>Verification TXT</strong>
                  </div>
                  <code className={styles.dns_value}>
                    {selectedDomainData?.verification_token}
                  </code>
                </div>
              </div>

              <div className={styles.dns_col}>
                <div className={styles.dns_record}>
                  <div className={styles.dns_header}>
                    <strong>DKIM</strong>
                  </div>

                  {selectedDomainData?.dkim_tokens?.map((t, i) => (
                    <code key={i} className={styles.dns_value}>
                      {t}
                    </code>
                  ))}
                </div>
              </div>
            </div>
          </>
        )}
        <div className={styles.dns_footer}>
          <button className={styles.primary_button} onClick={() => setSlide(2)}>
            {_("got_it")}
          </button>
        </div>
      </div>
    );
  };
  return (
    <Modal
      isOpen={isOpen}
      inProcess={inProcess}
      ariaHideApp={false}
      onConfirm={handleSave}
      onCancel={handleCloseNLModal}
      className={{
        base: classNames(styles.modalContent),
        afterOpen: styles.modalContentAfterOpen,
        beforeClose: styles.modalContentBeforeClose,
      }}
      overlayClassName={styles.modalOverlay}
    >
      <div className={styles.container}>
        {slide !== 3 && (
          <div className={styles.modalHeader}>
            <p className={styles.headerTitle}>{title}</p>
            <div className={styles.close} onClick={handleCloseNLModal}>
              <IconClose size={14} />
            </div>
          </div>
        )}
        <div
          className={classNames(
            `grid-x ${styles.modalBody}`,
            slide === 1 ? styles.first_slide : styles.second_slide
          )}
        >
          {slide === 1 && (
            <>
              <div className={"cell small-12 medium-3"}>
                <div>
                  <ul>
                    <li>
                      <span>
                        <h3> {_("plans_newsletter_title")} </h3>
                      </span>
                    </li>

                    <li className={styles.desc}>
                      <Tippy
                        content={<span> {_("plans_newsletter_desc")}</span>}
                      >
                        <span className={styles.tooltip}>
                          {_("plans_newsletter_desc")}
                        </span>
                      </Tippy>
                    </li>

                    <div className={styles.preferences}>
                      {preferences.map((preference, index) => (
                        <li key={`${index}`}>
                          {preference.desc && <h4> {preference.desc}: </h4>}
                          <ul>
                            {preference.details.map((detail, i) => (
                              <li
                                className={styles.options}
                                key={`pDetail-${i}`}
                              >
                                <span>
                                  {detail.option}
                                  <Tippy content={<span> {detail.desc}</span>}>
                                    <span className={styles.tooltip}>
                                      <i className="icon-ttp-alert-circle" />
                                    </span>
                                  </Tippy>
                                </span>
                              </li>
                            ))}
                          </ul>
                        </li>
                      ))}
                    </div>
                  </ul>
                </div>
              </div>
              <PlansNewsletter
                newsletterPlan={newsletterPlan}
                handleChangePlan={setNewsletterPlan}
                frequency={frequency}
                setFrequency={setFrequency}
                frequencyTabs={frequency_tabs}
                selectedDay={selectedDay}
                setSelectedDay={setSelectedDay}
                isAutomaticNewsletter={isAutomaticNewsletter}
                setIsAutomaticNewsletter={setIsAutomaticNewsletter}
                selectedWeek={selectedWeek}
                setSelectedWeek={setSelectedWeek}
                selectedHour={selectedHour}
                setSelectedHour={setSelectedHour}
                selectedMinutes={selectedMinutes}
                setSelectedMinutes={setSelectedMinutes}
                collaborators={data?.data}
                senders={senders}
                setSenders={setSenders}
                setSlide={setSlide}
                isLogoUaVisible={isLogoUaVisible}
                setIsLogoUaVisible={setIsLogoUaVisible}
                basicPlan={basicPlan}
              />
            </>
          )}
          {slide === 2 && (
            <NewsletterSettings
              newsletterPlan={newsletterPlan}
              frequency={frequency}
              setFrequency={setFrequency}
              frequencyTabs={frequency_tabs}
              selectedDay={selectedDay}
              setSelectedDay={setSelectedDay}
              isAutomaticNewsletter={isAutomaticNewsletter}
              setIsAutomaticNewsletter={setIsAutomaticNewsletter}
              selectedWeek={selectedWeek}
              setSelectedWeek={setSelectedWeek}
              selectedHour={selectedHour}
              setSelectedHour={setSelectedHour}
              selectedMinutes={selectedMinutes}
              setSelectedMinutes={setSelectedMinutes}
              collaborators={data?.data}
              senders={senders}
              setSenders={setSenders}
              setSlide={setSlide}
              verifiedEmailsList={verifiedEmailsList}
              handleFetchVerifiedEmails={handleFetchVerifiedEmails}
              isVerifiedEmailsLoading={isVerifiedEmailsLoading}
              isLogoUaVisible={isLogoUaVisible}
              setIsLogoUaVisible={setIsLogoUaVisible}
              activeFrequencyTab={activeFrequencyTab}
              setActiveFrequencyTab={setActiveFrequencyTab}
              activeConfig={activeConfig}
              updateActiveConfig={updateActiveConfig}
              domains={domains}
              handleVerifyDomain={handleVerifyDomain}
              setSelectedDomain={setSelectedDomain}
              isVerifying={isVerifying}
              setIsVerifying={setIsVerifying}
            />
          )}
          {slide === 3 && renderDNSView()}
        </div>
        {slide !== 3 && (
          <>
            <hr className={styles.separator} />
            <div className={styles.modalFooter}>
              <div>
                <button
                  className={styles.cancelButton}
                  onClick={handleCloseNLModal}
                >
                  {_("cancel")}
                </button>
              </div>
              <div className={styles.right_block}>
                {slide === 1 && (
                  <Button
                    className={styles.configureButton}
                    onClick={() => setSlide(2)}
                  >
                    {_("configure_automatic_sending")}
                  </Button>
                )}

                <Button
                  className={styles.saveButton}
                  onClick={() => handleSave()}
                >
                  {_("save")}
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default NewsletterModal;
